"use client"

import { Card, CardContent } from "@/components/ui/card"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { AlertCircle } from "lucide-react"
import { HeroSection } from "@/components/ui/hero-section"

export function TermsOfServiceContent() {
  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative px-4 pt-6 md:pt-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-2">
            <BreadcrumbComponent items={[{ label: "Home", href: "/" }, { label: "Terms of Service" }]} />
          </div>

          <HeroSection
            size="minimal"
            showSparkle={false}
            showGlow={false}
            title="Terms of Service"
            subtitle="Last updated: July 19, 2026"
          />

          <Card className="bg-white/95 backdrop-blur-xs border-green-200 shadow-2xl">
            <CardContent className="p-4 sm:p-6 md:p-8 lg:p-12">
              <div className="prose prose-lg max-w-none">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-8">
                  <div className="flex items-start">
                    <AlertCircle className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-blue-900 mb-2">Important Notice</h3>
                      <p className="text-blue-800">
                        By using What Grass Is This?, you agree to these terms. Please read them carefully before using our
                        service.
                      </p>
                    </div>
                  </div>
                </div>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Service Description</h2>
                <p className="text-gray-700 mb-6">
                  What Grass Is This? provides personalized lawn care recommendations based on AI grass type identification and user
                  preferences. Our service includes photo analysis, science-backed care plans, educational content, a moderated Community lawn-photo gallery, and optional quote-request forms for lawn and home services.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. User Responsibilities</h2>
                <ul className="list-disc pl-6 mb-8 space-y-2 text-gray-700">
                  <li>Provide accurate information about your lawn and location</li>
                  <li>Use the service for personal, non-commercial purposes</li>
                  <li>Follow lawn care recommendations at your own discretion</li>
                  <li>Respect intellectual property rights</li>
                  <li>Submit only Community content that you created or have permission to share</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Community Content</h2>
                <p className="text-gray-700 mb-4">
                  You keep ownership of photos and other content you submit to the Community gallery. By submitting content for publication, you grant What Grass Is This? a non-exclusive, worldwide, royalty-free license to host, store, resize, reproduce, display, and distribute that content as needed to operate and promote the gallery and let visitors share links to it.
                </p>
                <p className="text-gray-700 mb-6">
                  Community submissions are reviewed before publication. We may reject, hide, or remove content that is unlawful, unsafe, misleading, abusive, promotional, unrelated to lawns, or otherwise unsuitable for the gallery. Do not include private information in a title, lawn story, project details, or social handle. You may request removal of your post by contacting support@whatgrassisthis.com.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. AI Analysis Disclaimer</h2>
                <p className="text-gray-700 mb-6">
                  Our AI grass identification system provides estimates based on visual analysis. Results may vary and
                  should be considered as guidance rather than definitive identification. We recommend consulting with
                  local lawn care professionals for complex issues.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Limitation of Liability</h2>
                <p className="text-gray-700 mb-6">
                  What Grass Is This? provides recommendations for informational purposes only. We are not responsible for any
                  damage to your lawn or property resulting from following our recommendations. Always test products on
                  a small area first.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Product Recommendations</h2>
                <p className="text-gray-700 mb-6">
                  We may recommend third-party products and services. These recommendations are based on general
                  suitability and we may receive compensation from affiliate partnerships. Always read product labels
                  and follow manufacturer instructions.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Quote Requests and Service Providers</h2>
                <p className="text-gray-700 mb-6">
                  If you submit a quote request, you agree that What Grass Is This? may contact you about the request
                  and may share your request information with one or more relevant lawn or home service providers after
                  manual review. What Grass Is This? is not the service provider for those jobs unless we separately say
                  so in writing. We do not guarantee provider availability, response times, pricing, qualifications,
                  estimates, completed work, or the outcome of any provider interaction. Any service agreement, payment,
                  scheduling, warranty, dispute, or project work you enter with a provider is between you and that
                  provider.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Subscriptions and Billing</h2>
                <p className="text-gray-700 mb-4">
                  What Grass Is This? offers an optional premium subscription (What Grass Is This? Premium) that unlocks
                  features such as your full personalized season plan, unlimited grass and problem scans, and timed care
                  reminders. Premium is offered as an auto-renewable subscription on a monthly or annual basis. The exact
                  price, billing period, and any free trial are shown in the app at the point of purchase and may vary by
                  region and over time.
                </p>
                <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">
                  <li>Payment is charged to your Apple ID account at confirmation of purchase.</li>
                  <li>
                    The subscription automatically renews unless it is canceled at least 24 hours before the end of the
                    current period. Your account is charged for renewal within 24 hours before the end of the current
                    period, at the price of the plan you selected.
                  </li>
                  <li>
                    You can manage your subscription and turn off auto-renewal in your App Store account settings after
                    purchase. Deleting the app does not cancel your subscription.
                  </li>
                  <li>
                    Where a free trial is offered, any unused portion is forfeited when you purchase a subscription to
                    that feature.
                  </li>
                </ul>
                <p className="text-gray-700 mb-6">
                  Use of the subscription is also governed by Apple&apos;s Standard End User License Agreement (EULA),
                  available at{" "}
                  <a
                    href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 font-semibold"
                  >
                    apple.com/legal/internet-services/itunes/dev/stdeula
                  </a>
                  . Subscriptions purchased through the App Store are billed and managed by Apple, and refunds are handled
                  by Apple in accordance with their policies.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Account Termination</h2>
                <p className="text-gray-700 mb-6">
                  You may delete your account at any time. We reserve the right to suspend or terminate accounts that
                  violate these terms or engage in harmful behavior.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Changes to Terms</h2>
                <p className="text-gray-700 mb-6">
                  We may update these terms periodically. Continued use of the service after changes constitutes
                  acceptance of the new terms.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Contact Information</h2>
                <p className="text-gray-700">
                  For questions about these terms, contact us at{" "}
                  <a href="mailto:support@whatgrassisthis.com" className="text-green-600 hover:text-green-700 font-semibold">
                    support@whatgrassisthis.com
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
